import { useEffect, useState } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import {
  Box, IconButton, Tooltip, Typography, Button,
  MenuItem, Select, FormControl, InputLabel, FormHelperText,
  Chip, CircularProgress, Alert,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Hub as ChannelIcon,
  Business as OrgIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
} from '@mui/icons-material'
import { themeColors } from '../../app/layout/theme'
import { channelSchema, channelCreateSchema, type ChannelFormData } from '../../lib/schemas/channelSchema'
import {
  CHANNEL_TYPES, CHANNEL_TYPE_LABELS, CHANNEL_TYPE_COLORS, CRM_LEAD_FIELDS, queueApi,
} from '../../lib/api/channelsApi'
import { getCustomerTypes } from '../../lib/api/customerTypesApi'
import { orgsApi, lobsApi } from '../../lib/api/tenantsApi'
import { authStore } from '../../lib/stores/authStore'
import { useOrgSelector } from '../../context/OrgSelectorContext'
import {
  EntityFormDrawer,
  EntityFormSection,
  EntityDrawerInput,
} from '../shared/EntityFormDrawer'

// ── Pre-built flat MenuItem list (module scope = no JSX-in-callback issues) ──

const CRM_FIELD_MENU_ITEMS = CRM_LEAD_FIELDS.map(f => ({
  value:    f.value,
  label:    f.label,
  dbColumn: f.dbColumn,
  required: f.required,
}))

/** CRM fields whose value is chosen by the user (static), never read from the payload */
const STATIC_VALUE_FIELDS = ['customer_type_id']

// ─────────────────────────────────────────────────────────────────────────────

interface ChannelDrawerProps {
  open:          boolean
  onClose:       () => void
  onSubmit:      (data: ChannelFormData) => void
  isSubmitting?: boolean
  initialData?:  Partial<ChannelFormData>
  mode?:         'create' | 'edit'
}

const DEFAULT_VALUES: ChannelFormData = {
  name:                 '',
  type:                 'META',
  accessToken:          '',
  refreshToken:         '',
  webhookSecret:        '',
  apiBaseUrl:           '',
  syncFrequencyMinutes: 5,
  orgId:                '',
  lobId:                '',
  fieldMappings:        [],
}

export function ChannelDrawer({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialData,
  mode = 'create',
}: ChannelDrawerProps) {
  const {
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm<ChannelFormData>({
    // Org/LOB are mandatory only when creating a new channel — editing one never
    // shows the Scope section, so don't block saves on legacy channels that may
    // have a null org/lob from before this requirement existed.
    resolver: zodResolver(mode === 'create' ? channelCreateSchema : channelSchema),
    defaultValues: DEFAULT_VALUES,
  })

  const { fields: mappingFields, append: appendMapping, remove: removeMapping } = useFieldArray({
    control,
    name: 'fieldMappings',
  })

  const selectedType = watch('type')

  // SuperAdmin detection: OrgId/LobId-based (matches the navbar org selector), not
  // a roleName-string match — that doesn't reliably match every SuperAdmin's role
  // label, which previously hid the entire Organisation/LOB scope section here.
  const { isSuperAdmin } = useOrgSelector()
  const isRestApi    = selectedType === 'REST_API'

  // Test connection state
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'ok' | 'fail'>('idle')
  const [testMessage, setTestMessage] = useState('')

  // Customer types — used when "Customer Type" is picked as the DB column: the
  // value is a fixed selection (staticValue), never read from the payload.
  const { data: customerTypes = [] } = useQuery({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    enabled: open,
    staleTime: 60_000,
  })

  // Live row values (fields from useFieldArray don't re-render on value change)
  const watchedMappings = watch('fieldMappings') ?? []

  const handleTestConnection = async () => {
    const url   = watch('apiBaseUrl') ?? ''
    const token = watch('accessToken') ?? ''
    if (!url) return
    setTestStatus('testing')
    try {
      const result = await queueApi.testConnection(url, token)
      setTestStatus(result.ok ? 'ok' : 'fail')
      setTestMessage(result.message)
    } catch {
      setTestStatus('fail')
      setTestMessage('Connection failed')
    }
  }

  // Reset test status when url/token changes
  useEffect(() => { setTestStatus('idle') }, [watch('apiBaseUrl'), watch('accessToken')])

  // Fetch orgs only for SuperAdmin and only in create mode
  const { data: orgs = [], isLoading: orgsLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn:  orgsApi.getAll,
    enabled:  isSuperAdmin && mode === 'create',
    staleTime: 5 * 60_000,
  })

  const selectedOrgId = watch('orgId')
  const resolvedOrgId = isSuperAdmin ? selectedOrgId : authStore.user?.orgId

  // Fetch LOBs based on selected org (or user's own org) in create mode
  const { data: lobs = [], isLoading: lobsLoading } = useQuery({
    queryKey: ['lobs', resolvedOrgId],
    queryFn:  () => lobsApi.getAll(resolvedOrgId),
    enabled:  mode === 'create' && !!resolvedOrgId,
    staleTime: 5 * 60_000,
  })

  // Clear lobId field when selected org changes
  useEffect(() => {
    setValue('lobId', '')
  }, [selectedOrgId, setValue])

  useEffect(() => {
    if (open) {
      const base = initialData ? { ...DEFAULT_VALUES, ...initialData } : DEFAULT_VALUES
      // Admin's organisation is fixed and never shown as an editable field (only LOB
      // is) — pre-fill it from their own profile so the create-mode "Organisation is
      // required" validation passes without needing a field they can't change anyway.
      const orgId = mode === 'create' && !isSuperAdmin
        ? (authStore.user?.orgId || base.orgId)
        : base.orgId
      reset({ ...base, orgId })
    }
  }, [open, initialData, reset, mode, isSuperAdmin])

  const typeColor = CHANNEL_TYPE_COLORS[selectedType] ?? themeColors.primary

  return (
    <EntityFormDrawer
      open={open}
      title={mode === 'edit' ? 'Edit Channel' : 'New Channel Integration'}
      icon={<ChannelIcon sx={{ fontSize: 20, color: typeColor }} />}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={mode === 'edit' ? 'Save Changes' : 'Create Channel'}
      isSubmitting={isSubmitting}
      width={480}
    >
      {/* ── Basic Info ─────────────────────────────────────────────────────── */}
      <EntityFormSection title="Basic Information" columns={1}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <EntityDrawerInput
              label="Channel Name *"
              placeholder="e.g. Facebook Lead Ads – India"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={errors.name?.message}
              fullWidth
            />
          )}
        />

        <Box>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.type}>
                <InputLabel sx={{ fontSize: '13px' }}>Channel Type *</InputLabel>
                <Select
                  label="Channel Type *"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  sx={{
                    borderRadius: '8px', fontSize: '13px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: themeColors.primary, borderWidth: '1px',
                    },
                  }}
                >
                  {CHANNEL_TYPES.map(t => (
                    <MenuItem key={t} value={t} sx={{ fontSize: '13px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: CHANNEL_TYPE_COLORS[t], flexShrink: 0,
                        }} />
                        {CHANNEL_TYPE_LABELS[t]}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.type && <FormHelperText>{errors.type.message}</FormHelperText>}
              </FormControl>
            )}
          />
          <Chip
            label={CHANNEL_TYPE_LABELS[selectedType]}
            size="small"
            sx={{
              mt: 1, height: 20, fontSize: '10.5px', fontWeight: 600,
              bgcolor: `${typeColor}18`, color: typeColor,
              border: `1px solid ${typeColor}40`,
              '& .MuiChip-label': { px: '6px' },
            }}
          />
        </Box>
      </EntityFormSection>

      {/* ── Scope Scoping (create mode only) ───────────────────────────────── */}
      {mode === 'create' && (
        <EntityFormSection title="Integration Scope" columns={1}>
          {isSuperAdmin ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <OrgIcon sx={{ fontSize: 14, color: themeColors.text.muted }} />
                <Typography sx={{ fontSize: '11.5px', color: themeColors.text.muted, lineHeight: 1.6 }}>
                  Select which organisation owns this channel. Leads received via this channel will be scoped to that org.
                </Typography>
              </Box>

              <Controller
                name="orgId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.orgId}>
                    <InputLabel sx={{ fontSize: '13px' }}>Organisation *</InputLabel>
                    <Select
                      label="Organisation *"
                      value={field.value ?? ''}
                      onChange={e => field.onChange(e.target.value)}
                      startAdornment={
                        orgsLoading
                          ? <CircularProgress size={12} sx={{ mr: 1 }} />
                          : undefined
                      }
                      sx={{
                        borderRadius: '8px', fontSize: '13px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: themeColors.primary, borderWidth: '1px',
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: '13px', color: themeColors.text.muted }}>
                        Select organisation…
                      </MenuItem>
                      {orgs.map(org => (
                        <MenuItem key={org.id} value={org.id} sx={{ fontSize: '13px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <OrgIcon sx={{ fontSize: 14, color: themeColors.text.muted }} />
                            {org.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.orgId && <FormHelperText>{errors.orgId.message}</FormHelperText>}
                  </FormControl>
                )}
              />

              <Controller
                name="lobId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.lobId} disabled={!selectedOrgId} sx={{ mt: 1 }}>
                    <InputLabel sx={{ fontSize: '13px' }}>Line of Business *</InputLabel>
                    <Select
                      label="Line of Business *"
                      value={field.value ?? ''}
                      onChange={e => field.onChange(e.target.value)}
                      startAdornment={
                        lobsLoading
                          ? <CircularProgress size={12} sx={{ mr: 1 }} />
                          : undefined
                      }
                      sx={{
                        borderRadius: '8px', fontSize: '13px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: themeColors.primary, borderWidth: '1px',
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: '13px', color: themeColors.text.muted }}>
                        {selectedOrgId ? 'Select line of business…' : 'Select organisation first…'}
                      </MenuItem>
                      {lobs.map(lob => (
                        <MenuItem key={lob.id} value={lob.id} sx={{ fontSize: '13px' }}>
                          {lob.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.lobId && <FormHelperText>{errors.lobId.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <OrgIcon sx={{ fontSize: 14, color: themeColors.text.muted }} />
                <Typography sx={{ fontSize: '11.5px', color: themeColors.text.muted, lineHeight: 1.6 }}>
                  Select the Line of Business (LOB) for this channel.
                </Typography>
              </Box>

              <Controller
                name="lobId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.lobId}>
                    <InputLabel sx={{ fontSize: '13px' }}>Line of Business *</InputLabel>
                    <Select
                      label="Line of Business *"
                      value={field.value ?? ''}
                      onChange={e => field.onChange(e.target.value)}
                      startAdornment={
                        lobsLoading
                          ? <CircularProgress size={12} sx={{ mr: 1 }} />
                          : undefined
                      }
                      sx={{
                        borderRadius: '8px', fontSize: '13px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: themeColors.primary, borderWidth: '1px',
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: '13px', color: themeColors.text.muted }}>
                        Select line of business…
                      </MenuItem>
                      {lobs.map(lob => (
                        <MenuItem key={lob.id} value={lob.id} sx={{ fontSize: '13px' }}>
                          {lob.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.lobId && <FormHelperText>{errors.lobId.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </>
          )}
        </EntityFormSection>
      )}

      {/* ── API Credentials ────────────────────────────────────────────────── */}
      <EntityFormSection title="API Credentials" columns={1}>
        <Typography sx={{ fontSize: '11.5px', color: themeColors.text.muted, lineHeight: 1.6 }}>
          Credentials for {CHANNEL_TYPE_LABELS[selectedType]}. Stored encrypted at rest.
        </Typography>

        <Controller
          name="accessToken"
          control={control}
          render={({ field }) => (
            <EntityDrawerInput
              label="Access Token / API Key"
              placeholder="Enter access token or API key"
              type="password"
              value={field.value ?? ''}
              onChange={field.onChange}
              fullWidth
            />
          )}
        />

        <Controller
          name="refreshToken"
          control={control}
          render={({ field }) => (
            <EntityDrawerInput
              label="Refresh Token"
              placeholder="Refresh token (if applicable)"
              type="password"
              value={field.value ?? ''}
              onChange={field.onChange}
              fullWidth
            />
          )}
        />

        {!isRestApi && (
          <>
            <Controller
              name="webhookSecret"
              control={control}
              render={({ field }) => (
                <EntityDrawerInput
                  label="Webhook Secret"
                  placeholder="Secret to validate incoming webhooks"
                  type="password"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  fullWidth
                />
              )}
            />

            <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Typography sx={{ fontSize: '11px', color: '#15803d', lineHeight: 1.6 }}>
                <strong>Webhook URL</strong> is auto-generated after the channel is saved.
                Paste it into your {CHANNEL_TYPE_LABELS[selectedType]} dashboard to receive leads.
              </Typography>
            </Box>
          </>
        )}
      </EntityFormSection>

      {/* ── REST API Settings (REST_API type only) ────────────────────────── */}
      {isRestApi && (
        <EntityFormSection title="REST API Settings" columns={1}>
          <Typography sx={{ fontSize: '11.5px', color: themeColors.text.muted, lineHeight: 1.6 }}>
            Configure the external REST API endpoint. The sync service will call{' '}
            <code style={{ fontFamily: 'monospace', fontSize: '11px' }}>GET {'{API Base URL}'}/leads?created_after={'{timestamp}'}</code>{' '}
            on the configured frequency.
          </Typography>

          <Controller
            name="apiBaseUrl"
            control={control}
            render={({ field }) => (
              <EntityDrawerInput
                label="API Base URL *"
                placeholder="https://api.yourplatform.com/v1"
                value={field.value ?? ''}
                onChange={e => { field.onChange(e); setTestStatus('idle') }}
                error={errors.apiBaseUrl?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="syncFrequencyMinutes"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>Sync Frequency</InputLabel>
                <Select
                  label="Sync Frequency"
                  value={field.value ?? 5}
                  onChange={e => field.onChange(Number(e.target.value))}
                  sx={{
                    borderRadius: '8px', fontSize: '13px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary, borderWidth: '1px' },
                  }}
                >
                  {[1, 2, 5, 10, 15, 30, 60, 120, 360, 720, 1440].map(m => (
                    <MenuItem key={m} value={m} sx={{ fontSize: '13px' }}>
                      {m < 60 ? `${m} minute${m > 1 ? 's' : ''}` : `${m / 60} hour${m / 60 > 1 ? 's' : ''}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {/* Test Connection */}
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={testStatus === 'testing'
                ? <CircularProgress size={13} />
                : testStatus === 'ok' ? <CheckIcon sx={{ fontSize: 14, color: '#10b981' }} />
                : testStatus === 'fail' ? <ErrorIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                : <LinkIcon sx={{ fontSize: 14 }} />}
              disabled={testStatus === 'testing' || !watch('apiBaseUrl')}
              onClick={handleTestConnection}
              sx={{
                fontSize: '12px', textTransform: 'none', borderRadius: '8px',
                borderColor: testStatus === 'ok' ? '#10b981' : testStatus === 'fail' ? '#ef4444' : themeColors.border,
                color: testStatus === 'ok' ? '#10b981' : testStatus === 'fail' ? '#ef4444' : themeColors.primary,
              }}
            >
              {testStatus === 'testing' ? 'Testing…' : testStatus === 'ok' ? 'Connected' : testStatus === 'fail' ? 'Failed' : 'Test Connection'}
            </Button>
            {testMessage && testStatus !== 'idle' && (
              <Alert
                severity={testStatus === 'ok' ? 'success' : 'error'}
                sx={{ mt: 1, py: '4px', fontSize: '12px', '& .MuiAlert-message': { py: 0 } }}
              >
                {testMessage}
              </Alert>
            )}
          </Box>
        </EntityFormSection>
      )}

      {/* ── Field Mappings ─────────────────────────────────────────────────── */}
      <EntityFormSection title="Lead Field Mapping" columns={1}>
        <Typography sx={{ fontSize: '11.5px', color: themeColors.text.muted, lineHeight: 1.6 }}>
          Map incoming platform field names to{' '}
          <code style={{ fontFamily: 'monospace', fontSize: '11px' }}>public.leads</code>{' '}
          columns. You can configure this fully after creation on the detail page.
        </Typography>

        {mappingFields.map((mf, index) => {
          const isStaticRow = STATIC_VALUE_FIELDS.includes(watchedMappings[index]?.crmField ?? '')

          return (
          <Box key={mf.id} sx={{
            display: 'grid', gridTemplateColumns: '1fr 1fr auto',
            gap: 1, alignItems: 'flex-start',
          }}>
            {/* Platform Field — or a static Customer Type dropdown */}
            {isStaticRow ? (
              <Box>
                {index === 0 && (
                  <Typography sx={{
                    fontSize: '12px', fontWeight: 600,
                    color: themeColors.text.primary, mb: '6px',
                  }}>
                    Customer Type
                  </Typography>
                )}
                <Controller
                  name={`fieldMappings.${index}.staticValue`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.fieldMappings?.[index]?.platformField}>
                      <Select
                        value={field.value ?? ''}
                        onChange={e => field.onChange(String(e.target.value))}
                        displayEmpty
                        sx={{
                          borderRadius: '8px', fontSize: '12.5px',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: themeColors.primary, borderWidth: '1px',
                          },
                        }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: '12px', color: themeColors.text.muted }}>
                          Select customer type…
                        </MenuItem>
                        {customerTypes.map(ct => (
                          <MenuItem key={ct.id} value={String(ct.id)} sx={{ fontSize: '12.5px' }}>
                            {ct.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.fieldMappings?.[index]?.platformField && (
                        <FormHelperText>Select a customer type</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
            ) : (
            <Controller
              name={`fieldMappings.${index}.platformField`}
              control={control}
              render={({ field }) => (
                <EntityDrawerInput
                  label={index === 0 ? 'Platform Field' : undefined}
                  placeholder="e.g. full_name"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.fieldMappings?.[index]?.platformField?.message}
                />
              )}
            />
            )}

            {/* CRM Field — flat list (grouped view is on detail page) */}
            <Box>
              {index === 0 && (
                <Typography sx={{
                  fontSize: '12px', fontWeight: 600,
                  color: themeColors.text.primary, mb: '6px',
                }}>
                  DB Column
                </Typography>
              )}
              <Controller
                name={`fieldMappings.${index}.crmField`}
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <Select
                      value={field.value || ''}
                      onChange={e => {
                        const next = e.target.value
                        field.onChange(next)
                        // Static fields never read from the payload; payload fields
                        // never carry a static value.
                        if (STATIC_VALUE_FIELDS.includes(next)) {
                          setValue(`fieldMappings.${index}.platformField`, '')
                        } else {
                          setValue(`fieldMappings.${index}.staticValue`, '')
                        }
                      }}
                      displayEmpty
                      sx={{
                        borderRadius: '8px', fontSize: '12.5px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: themeColors.primary, borderWidth: '1px',
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: '12px', color: themeColors.text.muted }}>
                        Select column…
                      </MenuItem>
                      {CRM_FIELD_MENU_ITEMS.map(f => (
                        <MenuItem key={f.value} value={f.value} sx={{ fontSize: '12px', py: '4px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                              {f.label}
                            </Typography>
                            {f.required && (
                              <Typography sx={{ fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>
                                *
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: '#94a3b8' }}>
                              {f.dbColumn}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            {/* Remove */}
            <Box sx={{ mt: index === 0 ? '26px' : '2px' }}>
              <Tooltip title="Remove mapping">
                <IconButton size="small" onClick={() => removeMapping(index)}
                  sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fee2e2' } }}>
                  <DeleteIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          )
        })}

        <Box
          onClick={() => appendMapping({ platformField: '', crmField: '', staticValue: '' })}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            p: 1, borderRadius: '8px', cursor: 'pointer',
            border: `1.5px dashed ${themeColors.border}`,
            color: themeColors.primary, mt: 0.5,
            '&:hover': { bgcolor: themeColors.primaryLight + '15', borderColor: themeColors.primary },
            transition: 'all 0.15s',
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '12.5px', fontWeight: 600 }}>
            Add Field Mapping
          </Typography>
        </Box>
      </EntityFormSection>
    </EntityFormDrawer>
  )
}
